<script setup lang="ts">
// One panel of the report, as it reads in the Analysis pane: its title, what it
// shows, and its explainer behind an info control. The report's prose — what
// the panel measures, its denominators, how to read it beside its neighbours —
// was most of a panel's own height; a modal holds it now, one click away and
// out of the reading flow, so a question's panels fit a rail's width and the
// operator reads the chart they asked for rather than the essay around it.
//
// A panel can also be read at full size: the analysis overlay adopts this very
// component instance by teleporting the section into its host, so the chart
// keeps its own resize observers and its drill-down keeps its closures. The
// panel owns the control because it is the only thing that knows its own title
// and the id the overlay keys on — the pane cannot reach into a card it does
// not render, and a second copy of the report markup would drift from this one.
import { computed, onBeforeUnmount, ref, useId } from "vue";
import {
	ANALYSIS_CARD_HOST_ID,
	cardHostGeneration,
	cardHostMounted,
	collapseCard,
	expandCard,
	expandedCardId,
} from "../cards/cardExpander";

withDefaults(
	defineProps<{
		/** The panel's own title, stated on it. */
		title: string;
		/** Whether the panel offers to be read at full size in the overlay.
		 *  Defaults on, so a caller that knows nothing of the feature still
		 *  gets it; a panel rendered outside the Analysis pane opts out. */
		expandable?: boolean;
	}>(),
	{ expandable: true },
);

// The overlay holds one card at a time and this panel is that card exactly when
// the ids match. useId keeps panels distinct even when two of them are titled
// the same, and stays stable across the teleport, so opening and closing never
// loses track of which panel is enlarged.
const id = `report-panel-${useId()}`;
const isOpen = computed(() => expandedCardId.value === id);

// A drill-down replaces the question's panels, and the panel it unmounts may be
// the enlarged one. Collapsing here means the overlay cannot be left holding an
// empty host — a layer with no card in it and nothing to close it by.
onBeforeUnmount(() => {
	if (isOpen.value) collapseCard();
});

// The explainer's modal. It belongs to this panel, so two panels on screen
// never share one.
const helpOpen = ref(false);
</script>

<template>
	<!-- Teleported only while enlarged: the overlay host is a persistent element
	     inside the layer, so moving the DOM there keeps this a live instance
	     (chart observers, handlers, drill-down) rather than a rendered copy.
	     While closed the teleport is disabled and the panel sits in the pane.
	     `defer` resolves the target after the whole tree has rendered: a panel
	     mounts before the layer that owns the host, and a Teleport caches the
	     target it resolved at mount — a missing host at that moment is cached as
	     null and then throws when the teleport is enabled. The key rebuilds the
	     teleport when a different layer mounts, because the cached target would
	     otherwise be that layer's removed host element. -->
	<Teleport
		:key="cardHostGeneration"
		defer
		:to="'#' + ANALYSIS_CARD_HOST_ID"
		:disabled="!isOpen"
	>
		<section class="report-panel">
			<div class="report-panel-head">
				<h4 class="report-panel-title">{{ title }}</h4>
				<!-- Gated on the host being mounted: ReportPanel is also rendered by
				     the waste panels, and a panel whose overlay host is absent must
				     not offer a control that would send its content into a target
				     that is not there — the panel would simply vanish. -->
				<button
					v-if="expandable && cardHostMounted && !isOpen"
					type="button"
					class="report-panel-expand"
					:title="`Open ${title} at full size`"
					:aria-label="`Open ${title} at full size`"
					@click="expandCard(id)"
				>
					<i class="bi bi-arrows-angle-expand"></i>
				</button>
				<button
					v-if="$slots.help"
					type="button"
					class="report-panel-info"
					title="What this panel measures"
					aria-label="What this panel measures"
					@click="helpOpen = true"
				>
					<i class="bi bi-info-circle"></i>
				</button>
			</div>

			<div class="report-panel-body"><slot /></div>
		</section>
	</Teleport>

	<Teleport to="body">
		<div
			v-if="helpOpen"
			class="modal fade show d-block report-help-modal"
			:class="{ 'report-panel-help-over-layer': isOpen }"
			tabindex="-1"
			@click.self="helpOpen = false"
		>
			<div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">
							<i class="bi bi-info-circle me-2"></i>{{ title }}
						</h5>
						<button
							type="button"
							class="btn-close"
							aria-label="Close"
							@click="helpOpen = false"
						></button>
					</div>
					<div class="modal-body report-help"><slot name="help" /></div>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<style scoped>
.report-panel {
	display: flex;
	flex-direction: column;
	min-width: 0;
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	padding: 8px 10px 10px;
}

/* The head is the title and its controls, so the controls sit where the reader
   is already looking and never over the chart. */
.report-panel-head {
	display: flex;
	align-items: flex-start;
	gap: 6px;
	margin-bottom: 4px;
}

.report-panel-title {
	flex: 1 1 auto;
	min-width: 0;
	margin: 0;
	font-size: 0.8rem;
	font-weight: 600;
	color: #212529;
}

.report-panel-info {
	flex: 0 0 auto;
	border: 0;
	background: transparent;
	padding: 0 2px;
	line-height: 1;
	color: #6c757d;
	cursor: pointer;
}

.report-panel-info:hover {
	color: #0d6efd;
}

/* The expander reads as the info control's twin: same size, same muted grey,
   so the head stays calm and neither icon competes with the title. */
.report-panel-expand {
	flex: 0 0 auto;
	border: 0;
	background: transparent;
	padding: 0 2px;
	line-height: 1;
	color: #6c757d;
	font-size: 0.72rem;
	cursor: pointer;
}

.report-panel-expand:hover {
	color: #0d6efd;
}

/* The overlay layer sits at z-index 2000 while Bootstrap's .modal is 1055, so an
   explainer opened from an enlarged panel would render behind the layer. Raised
   past the layer only in that case: a normal panel's explainer deliberately
   keeps Bootstrap's own stacking against everything else on the page. Both
   classes are on the element, so this scoped selector out-specifies .modal. */
.report-help-modal.report-panel-help-over-layer {
	z-index: 2100;
}

.report-panel-body {
	display: flex;
	flex-direction: column;
	min-width: 0;
	min-height: 0;
}

/* The explainer, read as prose rather than as a chart's footnote: the panel's
   own scale is 0.7rem, which is a caption size, and a modal is a reading
   surface. The paragraphs are the caller's markup, so they are reached through
   the scope boundary. */
.report-help :deep(p) {
	margin: 0 0 10px;
	font-size: 0.85rem;
	line-height: 1.5;
	color: #212529;
}

.report-help :deep(p:last-child) {
	margin-bottom: 0;
}
</style>
